import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { postNewsletter } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await postNewsletter(email);
      toast.success("You’re on the list.", {
        description: "We’ll send occasional dispatches — never spam.",
      });
      setEmail("");
    } catch (err) {
      toast.error("Couldn’t subscribe", {
        description: "Please try again with a valid email.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-ink-olive text-bone relative overflow-hidden"
    >
      <div className="container-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="mono-label text-terracotta-soft">/ Newsletter</div>
          <h3 className="mt-4 font-serif italic text-4xl sm:text-5xl leading-[1.05] text-bone">
            Dispatches from the workshop.
          </h3>
          <p className="mt-4 text-bone/70 max-w-md">
            A quiet monthly note on what we’re building across LiveCuts and
            Flockfolio.
          </p>
          <form
            onSubmit={handleSubscribe}
            className="mt-8 flex items-center border border-bone/30 focus-within:border-bone transition"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-transparent px-4 py-4 text-bone placeholder:text-bone/40 focus:outline-none font-sans"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-button"
              className="px-5 py-4 bg-bone text-ink-olive font-sans font-medium tracking-tight hover:bg-terracotta hover:text-bone transition-colors disabled:opacity-60"
            >
              {loading ? "…" : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
          <div>
            <div className="mono-label text-bone/50 mb-4">Studio</div>
            <ul className="space-y-2">
              <FooterLink to="/about" testid="footer-about">About</FooterLink>
              <FooterLink to="/careers" testid="footer-careers">Careers</FooterLink>
              <FooterLink to="/contact" testid="footer-contact">Contact</FooterLink>
            </ul>
          </div>
          <div>
            <div className="mono-label text-bone/50 mb-4">Products</div>
            <ul className="space-y-2">
              <FooterLink to="/products/livecuts" testid="footer-freshcut">
                LiveCuts
              </FooterLink>
              <FooterLink to="/products/flockfolio" testid="footer-flockfolio">
                Flockfolio
              </FooterLink>
            </ul>
          </div>
          <div>
            <div className="mono-label text-bone/50 mb-4">Contact</div>
            <ul className="space-y-2 text-bone/80 text-sm">
              <li data-testid="footer-email">hr@thiruhanarayanoinnovations.com</li>
              <li data-testid="footer-phone">+91 78422 55558</li>
              <li data-testid="footer-address">
                3-7-400/DB/2, Sanjeevani Hills Rd 2,
                <br />
                Hyderguda, Hyderabad — 500048
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-bone/15">
        <div className="container-x py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="mono-label text-bone/50" data-testid="footer-copyright">
            © {new Date().getFullYear()} · Thiruhanarayano Innovations LLP
          </div>
          <div className="flex items-center gap-6">
            <a
              href="#"
              data-testid="footer-linkedin"
              className="text-sm text-bone/70 hover:text-bone inline-flex items-center gap-1"
            >
              LinkedIn <ArrowUpRight size={14} />
            </a>
            <a
              href="#"
              data-testid="footer-github"
              className="text-sm text-bone/70 hover:text-bone inline-flex items-center gap-1"
            >
              GitHub <ArrowUpRight size={14} />
            </a>
            <a
              href="#"
              data-testid="footer-instagram"
              className="text-sm text-bone/70 hover:text-bone inline-flex items-center gap-1"
            >
              Instagram <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLink({ to, testid, children }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="text-bone/80 hover:text-bone link-underline text-sm"
      >
        {children}
      </Link>
    </li>
  );
}
